import { useCallback, useEffect, useMemo, useState } from 'react'

const compareText = (a, b) => String(a || '').localeCompare(String(b || ''), undefined, { sensitivity: 'base' })

const compareNumber = (a, b) => Number(a || 0) - Number(b || 0)

const EMPTY_FILTERS = {
	grade: [],
	plan: [],
	counselor: [],
	assignments: [],
}

const FILTER_TITLES = {
	grade: 'Grade',
	plan: 'Kandor plan',
	counselor: 'Counsellor',
	assignments: 'Assignments',
}

const ASSIGNMENT_LABELS = {
	assigned: 'Has assignments',
	none: 'None',
}

const valueToLabel = (key, value) => {
	if (!value) return ''
	if (key === 'grade') return `Grade ${value}`
	if (key === 'assignments') return ASSIGNMENT_LABELS[value] || value
	return String(value)
}

export const useStudentsSection = ({ students, counselors, setStudents, onAssignSelected, onUnlinkStudents }) => {
	const [selectedIds, setSelectedIds] = useState([])
	const [search, setSearch] = useState('')
	const [cohort, setCohort] = useState('all')
	const [sortKey, setSortKey] = useState(null)
	const [sortDir, setSortDir] = useState('asc')
	const [sortLabel, setSortLabel] = useState('')
	const [isFilterOpen, setIsFilterOpen] = useState(false)
	const [draftFilters, setDraftFilters] = useState(EMPTY_FILTERS)
	const [appliedFilters, setAppliedFilters] = useState(EMPTY_FILTERS)
	const [chooserOpen, setChooserOpen] = useState(false)
	const [hoveredCounselorId, setHoveredCounselorId] = useState(null)
	const [isAssigning, setIsAssigning] = useState(false)
	const [deleteConfirm, setDeleteConfirm] = useState(null)
	const [isDeleting, setIsDeleting] = useState(false)
	const [actionsMenuId, setActionsMenuId] = useState(null)
	const [statusConfirm, setStatusConfirm] = useState(null)

	const counts = useMemo(() => {
		const unassigned = students.filter(row => row.counselor === 'Unassigned').length
		return {
			all: students.length,
			unassigned,
			assigned: students.length - unassigned,
		}
	}, [students])

	const filtered = useMemo(() => {
		const query = search.trim().toLowerCase()
		const next = students.filter(row => {
			if (query && !row.name.toLowerCase().includes(query)) return false
			if (cohort === 'unassigned' && row.counselor !== 'Unassigned') return false
			if (cohort === 'assigned' && row.counselor === 'Unassigned') return false
			if (appliedFilters.grade.length && !appliedFilters.grade.includes(row.grade)) return false
			if (appliedFilters.plan.length && !appliedFilters.plan.includes(row.plan)) return false
			if (appliedFilters.counselor.length && !appliedFilters.counselor.includes(row.counselor)) return false
			if (appliedFilters.assignments.includes('assigned') && !(row.assignments > 0)) return false
			if (appliedFilters.assignments.includes('none') && row.assignments > 0) return false
			return true
		})

		if (!sortKey) return next

		const sorted = [...next].sort((a, b) => {
			let result = 0
			if (sortKey === 'student') result = compareText(a.name, b.name)
			if (sortKey === 'grade') result = compareNumber(a.grade, b.grade)
			if (sortKey === 'plan') result = compareText(a.plan, b.plan)
			if (sortKey === 'counselor') result = compareText(a.counselor, b.counselor)
			if (sortKey === 'assignments') result = compareNumber(a.assignments, b.assignments)
			return sortDir === 'desc' ? -result : result
		})

		return sorted
	}, [appliedFilters, cohort, search, sortDir, sortKey, students])

	const selected = useMemo(() => new Set(selectedIds), [selectedIds])
	const hasSelection = selectedIds.length > 0
	const selectableRows = filtered.filter(row => !row.disabled)
	const allSelected = selectableRows.length > 0 && selectableRows.every(row => selected.has(row.id))

	const filterChips = useMemo(() => {
		const chips = Object.keys(FILTER_TITLES)
			.map(key => {
				const selected = appliedFilters[key] || []
				if (!selected.length) return null
				const labels = selected.map(value => valueToLabel(key, value)).filter(Boolean)
				if (!labels.length) return null
				return {
					id: `filter:${key}`,
					label: `${FILTER_TITLES[key]}: ${labels.join(', ')}`,
					kind: 'filter',
					filterKey: key,
				}
			})
			.filter(Boolean)

		if (sortKey && sortLabel) {
			chips.push({ id: 'sort', label: sortLabel, kind: 'sort' })
		}

		return chips
	}, [appliedFilters, sortKey, sortLabel])

	const counselorLoads = useMemo(
		() =>
			counselors.map(item => ({
				...item,
				workload:
					item.caseload != null
						? Number(item.caseload) || 0
						: students.filter(row => row.counselor === item.name).length,
			})),
		[counselors, students],
	)

	const toggleOne = useCallback(id => {
		if (students.find(student => student.id === id)?.disabled) return
		setSelectedIds(current => (current.includes(id) ? current.filter(item => item !== id) : [...current, id]))
	}, [students])

	const toggleAll = useCallback(() => {
		setSelectedIds(current => {
			if (allSelected) return current.filter(id => !selectableRows.some(row => row.id === id))
			return [...new Set([...current, ...selectableRows.map(row => row.id)])]
		})
	}, [allSelected, selectableRows])

	const toggleActionsMenu = useCallback(id => {
		setActionsMenuId(current => current === id ? null : id)
	}, [])

	useEffect(() => {
		if (!actionsMenuId) return undefined
		const closeOnOutsideClick = event => {
			if (!event.target.closest('[data-student-actions]')) {
				setActionsMenuId(null)
			}
		}
		document.addEventListener('mousedown', closeOnOutsideClick)
		return () => document.removeEventListener('mousedown', closeOnOutsideClick)
	}, [actionsMenuId])

	const requestToggleStudentDisabled = useCallback(student => {
		setActionsMenuId(null)
		setStatusConfirm({
			studentId: student.id,
			studentName: student.name,
			disabled: !student.disabled,
		})
	}, [])

	const cancelStatusConfirm = useCallback(() => setStatusConfirm(null), [])

	const confirmStatusChange = useCallback(() => {
		if (!statusConfirm) return
		setStudents(current => current.map(student => (
			student.id === statusConfirm.studentId
				? { ...student, disabled: statusConfirm.disabled }
				: student
		)))
		setSelectedIds(current => current.filter(id => id !== statusConfirm.studentId))
		setStatusConfirm(null)
	}, [setStudents, statusConfirm])

	const clearSelection = useCallback(() => setSelectedIds([]), [])

	const setSort = useCallback((key, dir, label) => {
		setSortKey(key)
		setSortDir(dir)
		setSortLabel(label)
	}, [])

	const clearSort = useCallback(() => {
		setSortKey(null)
		setSortDir('asc')
		setSortLabel('')
	}, [])

	const onChangeFilterKey = useCallback((key, values) => {
		setDraftFilters(current => ({ ...current, [key]: values }))
	}, [])

	const applyFilters = useCallback(() => {
		setAppliedFilters(draftFilters)
		setIsFilterOpen(false)
	}, [draftFilters])

	const removeOnePanelFilter = useCallback(filterKey => {
		if (!filterKey) return
		const next = { ...appliedFilters, [filterKey]: [] }
		setAppliedFilters(next)
		setDraftFilters(next)
	}, [appliedFilters])

	const clearAllPanelFilters = useCallback(() => {
		setAppliedFilters(EMPTY_FILTERS)
		setDraftFilters(EMPTY_FILTERS)
		clearSort()
	}, [clearSort])

	const removeFilterChip = useCallback(chip => {
		if (!chip) return
		if (chip.kind === 'sort') {
			clearSort()
			return
		}
		if (chip.kind === 'filter' && chip.filterKey) {
			removeOnePanelFilter(chip.filterKey)
		}
	}, [clearSort, removeOnePanelFilter])

	const openFilter = useCallback(() => {
		setDraftFilters(appliedFilters)
		setIsFilterOpen(true)
	}, [appliedFilters])

	const closeFilter = useCallback(() => setIsFilterOpen(false), [])

	const assignSelected = useCallback(
		async counselor => {
			if (!selectedIds.length || !counselor || isAssigning) return
			const studentIds = [...selectedIds]
			if (!onAssignSelected) return

			setIsAssigning(true)
			try {
				await onAssignSelected({ counselor, studentIds })
				setSelectedIds([])
				setChooserOpen(false)
			} finally {
				setIsAssigning(false)
			}
		},
		[isAssigning, onAssignSelected, selectedIds],
	)

	const removeFromSelection = useCallback(ids => {
		const remove = new Set((ids || []).map(id => String(id)))
		if (!remove.size) return
		setSelectedIds(current => current.filter(id => !remove.has(String(id))))
	}, [])

	const selectedStudents = useMemo(
		() => students.filter(row => selected.has(row.id)),
		[selected, students],
	)

	const requestRemoveStudent = useCallback(
		id => {
			const row = students.find(student => student.id === id)
			if (!row) return
			setActionsMenuId(null)
			setDeleteConfirm({
				studentIds: [id],
				title: `Delete ${row.name}?`,
			})
		},
		[students],
	)

	const requestRemoveSelected = useCallback(() => {
		if (!selectedIds.length) return
		const count = selectedIds.length
		setDeleteConfirm({
			studentIds: [...selectedIds],
			title: count === 1 ? 'Delete 1 student?' : `Delete ${count} students?`,
		})
	}, [selectedIds])

	const cancelDeleteConfirm = useCallback(() => {
		if (isDeleting) return
		setDeleteConfirm(null)
	}, [isDeleting])

	const confirmDelete = useCallback(async () => {
		if (!deleteConfirm?.studentIds?.length || !onUnlinkStudents || isDeleting) return
		const studentIds = [...deleteConfirm.studentIds]
		setIsDeleting(true)
		try {
			await onUnlinkStudents({ studentIds })
			const remove = new Set(studentIds.map(String))
			setSelectedIds(current => current.filter(id => !remove.has(String(id))))
			setDeleteConfirm(null)
		} finally {
			setIsDeleting(false)
		}
	}, [deleteConfirm, isDeleting, onUnlinkStudents])

	return {
		counts,
		filtered,
		selected,
		selectedIds,
		selectedStudents,
		hasSelection,
		allSelected,
		search,
		setSearch,
		cohort,
		setCohort,
		sortKey,
		sortDir,
		filterChips,
		isFilterOpen,
		draftFilters,
		chooserOpen,
		setChooserOpen,
		hoveredCounselorId,
		setHoveredCounselorId,
		counselorLoads,
		isAssigning,
		deleteConfirm,
		isDeleting,
		actionsMenuId,
		statusConfirm,
		toggleOne,
		toggleAll,
		clearSelection,
		setSort,
		clearSort,
		removeFilterChip,
		clearAllPanelFilters,
		onChangeFilterKey,
		applyFilters,
		openFilter,
		closeFilter,
		assignSelected,
		removeFromSelection,
		requestRemoveStudent,
		requestRemoveSelected,
		cancelDeleteConfirm,
		confirmDelete,
		toggleActionsMenu,
		requestToggleStudentDisabled,
		cancelStatusConfirm,
		confirmStatusChange,
	}
}
